import { CircleX, FileIcon, Paperclip, Download } from "lucide-react";
import PDFIcon from "@/assets/pdfIcon.svg";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import React, { useEffect, useState } from "react";
import axiosAuth from "@/services/axiosAuth";
import { FILE_UPLOAD } from "@/services/school/apiRoutes";

const FileInput = ({
  file,
  fileInputRef,
  setFile,
  accept,
  disabled,
  maxSize, // in mb
  fileName, // 👈 add this
}: any) => {
  const [displayName, setDisplayName] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);

  // Check if file is an attachment_id (string) vs File object
  const isAttachmentId = typeof file === "string" && file.length > 0;

  useEffect(() => {
  // 👇 Clean filename before showing
  const cleanFileName = (name: string) => {
    if (!name) return "";
    // remove ISO timestamp like "-2025-10-09T12:22:16.918Z"
    return name.replace(/-\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z/, "");
  };

  if (file?.name) {
    setDisplayName(cleanFileName(file.name));
  } else if (fileName) {
    setDisplayName(cleanFileName(fileName));
  } else if (file) {
    setDisplayName(cleanFileName(file));
  } else {
    setDisplayName("");
  }
  
}, [file, fileName]);

  const handleDownload = async (attachmentId: string) => {
    if (disabled || isDownloading) return;
    
    setIsDownloading(true);
    try {
      const response = await axiosAuth.get(`${FILE_UPLOAD}${attachmentId}`, {
        responseType: "blob",
      });

      if (response?.status === 200) {
        // Create blob link and trigger download
        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        // Try to get filename from headers, fallback to fileName or default
        const contentDisposition = response.headers["content-disposition"];
        let downloadFileName = fileName || "document.pdf";
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match?.[1]) {
            downloadFileName = match[1];
          }
        }

        link.setAttribute("download", downloadFileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error("There is an issue while downloading the file");
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "There is an issue while downloading the file"
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (disabled) return;
    
    // If file is an attachment_id (string), download it directly
    if (isAttachmentId && displayName) {
      e.preventDefault();
      e.stopPropagation();
      handleDownload(file);
    } else {
      // Otherwise, open file picker for new upload
      fileInputRef?.current?.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (selectedFile) {
      const fileSizeInMB = (selectedFile?.size / (1024 * 1024)).toFixed(2);
      if (maxSize && parseFloat(fileSizeInMB) > maxSize) {
        fileInputRef.current.value = null;
        toast.error(`File size should be less than ${maxSize} MB`, {
          className: "!bg-red-500 !text-white",
        });
      } else {
        setFile(selectedFile);
      }
    }
  };

  return (
    <div className="flex flex-col w-full max-w-md text-start">
      {/* File Input Field */}
      <div
        className={cn(
          "flex items-center justify-between w-full border border-gray-300 rounded-[4px] px-3 py-2 cursor-pointer hover:border-gray-400 h-[40px]",
          disabled ? "cursor-not-allowed" : ""
        )}
        onClick={handleClick}
      >
        <div className="flex flex-row gap-2 items-center">
          {displayName ? (
            accept?.includes("pdf") ? (
              <img src={PDFIcon} className="h-8 w-7" />
            ) : (
              <FileIcon className="text-g-500" />
            )
          ) : (
            ""
          )}
          <div className="flex flex-col">
            <span
              className={cn(
                "text-gray-400 text-sm",
                displayName ? "text-primary" : "",
                isAttachmentId && displayName ? "cursor-pointer hover:underline" : ""
              )}
            >
              {displayName || "Please attach"}
            </span>
            {file?.size && (
              <span className="text-[10px] text-g-300 -mt-1">
                size: {parseFloat((file?.size / 1024).toString()).toFixed(2)} KB
              </span>
            )}
            {isAttachmentId && isDownloading && (
              <span className="text-[10px] text-g-300 -mt-1">
                Downloading...
              </span>
            )}
          </div>
        </div>
        {displayName ? (
          <div className="flex items-center gap-2">
            {isAttachmentId && !disabled && (
              <Download
                className={cn(
                  "text-gray-400 h-5 w-5",
                  isDownloading ? "cursor-wait" : "cursor-pointer hover:text-primary"
                )}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDownload(file);
                }}
              />
            )}
            <CircleX
              className={cn(
                "text-gray-400 h-5 w-5",
                disabled ? "cursor-not-allowed" : "cursor-pointer"
              )}
              onClick={(e) => {
                if (!disabled) {
                  e.preventDefault();
                  e.stopPropagation();
                  setFile(null);
                  setDisplayName(""); // 👈 clear display
                }
              }}
            />
          </div>
        ) : (
          <Paperclip className="text-gray-400 h-5 w-5" />
        )}
      </div>

      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileChange}
        accept={accept}
      />
    </div>
  );
};

export default FileInput;
